import { useCallback, useEffect, useRef } from 'react';

import { BrowserPopup } from './BrowserPopup';
import { PopupConfig, PopupEventHandlers, PopupState } from './types';

export * from './types';

export const useBrowserPopup = () => {
  const activePopups = useRef<Map<Window, BrowserPopup>>(new Map());

  const close = useCallback((popup: Window) => {
    const instance = activePopups.current.get(popup);
    if (instance) {
      instance.cleanup();
      activePopups.current.delete(popup);
    }
  }, []);

  useEffect(() => {
    return () => {
      activePopups.current.forEach((instance) => instance.cleanup());
      activePopups.current.clear();
    };
  }, []);

  const attachEventHandlers = useCallback(
    (popup: Window, handlers: PopupEventHandlers, interval = 10) => {
      const instance = activePopups.current.get(popup);
      if (instance) {
        const handleStateChange = async (window: Window, state: PopupState, error?: Error) => {
          switch (state) {
            case PopupState.READY:
              await handlers.onReady?.(window);
              break;
            case PopupState.BLOCKED:
              handlers.onBlocked?.();
              close(window);
              break;
            case PopupState.ERROR:
              handlers.onError?.(error || new Error('Unknown error'));
              close(window);
              break;
            case PopupState.CLOSED:
              handlers.onClosed?.();
              close(window);
              break;
            case PopupState.COMPLETED:
              handlers.onCompleted?.();
              close(window);
              break;
          }
        };

        instance.startMonitoring(handleStateChange, interval);
        return () => instance.stopMonitoring();
      }
      return () => {};
    },
    [close]
  );

  const open = useCallback(
    (url: string, config: Partial<PopupConfig> = {}, handlers: PopupEventHandlers = {}) => {
      const popupInstance = BrowserPopup.create(url, config);
      const popupWindow = popupInstance.getWindow();
      activePopups.current.set(popupWindow, popupInstance);

      attachEventHandlers(popupWindow, handlers);
      return popupWindow;
    },
    [attachEventHandlers]
  );

  const getPopupState = useCallback((popup: Window) => {
    return activePopups.current.get(popup)?.getState();
  }, []);

  return {
    open,
    close,
    getPopupState,
    attachEventHandlers,
  };
};
